import { useEffect, useMemo, useState } from 'react';
import {
  createTemplateVersion,
  getKillSwitch,
  getNotificationAuditTrail,
  listDeliveryAttempts,
  listNotifications,
  listRecipientRoutes,
  listTemplates,
  requestNotification,
  retryNotification,
  setKillSwitch,
  upsertRecipientRoute,
} from '../../api/notifications';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  GroupLabel,
  HintNote,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  SectionCard,
  StatusBadge,
  TextInput,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type {
  DeliveryAttempt,
  NotificationAuditEvent,
  NotificationOutboxRow,
  NotificationOutboxStatus,
  NotificationTemplate,
  RecipientRouteMasked,
} from '../../types/domain';

const TABS = ['inbox', 'routes', 'templates', 'killswitch'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL: Record<Tab, string> = { inbox: 'Kotak Keluar', routes: 'Rute Penerima', templates: 'Template', killswitch: 'Kill Switch' };

/**
 * Notifications / WhatsApp Center (FASE 13, migration 0052/0053). Every
 * outbound message in this build goes through the real internal SANDBOX
 * provider — deterministic, no network call, never a production recipient.
 * "Terkirim (SANDBOX)" below always means SANDBOX_MOCK_PROVIDER accepted
 * it, never a real WhatsApp delivery — there is no production provider
 * credential wired up yet (BLOCKED_BY_PROVIDER).
 */
export default function NotificationsPage() {
  const { hasPermission } = usePermissions();
  const [tab, setTab] = useState<Tab>('inbox');

  return (
    <div>
      <PageHeader
        title="Notifikasi / WhatsApp"
        subtitle="Antrian pengiriman nyata (notification_outbox/delivery_attempts) melalui SANDBOX_MOCK_PROVIDER — belum ada kredensial provider produksi (BLOCKED_BY_PROVIDER), jadi tidak ada pesan yang benar-benar terkirim ke WhatsApp asli."
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <Button key={t} variant={tab === t ? 'primary' : 'secondary'} onClick={() => setTab(t)} style={{ padding: '0 14px' }}>
            {TAB_LABEL[t]}
          </Button>
        ))}
      </div>

      {tab === 'inbox' && <InboxTab canRequest={hasPermission('notification', 'request')} canRetry={hasPermission('notification', 'retry')} />}
      {tab === 'routes' && <RoutesTab canManage={hasPermission('notification', 'route_manage')} />}
      {tab === 'templates' && <TemplatesTab canManage={hasPermission('notification', 'template_manage')} />}
      {tab === 'killswitch' && <KillSwitchTab canManage={hasPermission('notification', 'kill_switch_manage')} />}
    </div>
  );
}

const STATUS_LABEL: Record<NotificationOutboxStatus, string> = {
  REQUESTED: 'Diminta / Menunggu',
  SENT: 'Terkirim (SANDBOX)',
  DEAD_LETTER: 'Dead-Letter',
  BLOCKED: 'Diblokir',
};

function InboxTab({ canRequest, canRetry }: { canRequest: boolean; canRetry: boolean }) {
  const [rows, setRows] = useState<NotificationOutboxRow[]>([]);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [routes, setRoutes] = useState<RecipientRouteMasked[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'ALL' | NotificationOutboxStatus>('ALL');
  const [selected, setSelected] = useState<NotificationOutboxRow | null>(null);
  const [showSandboxForm, setShowSandboxForm] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [n, t, r] = await Promise.all([listNotifications(), listTemplates(), listRecipientRoutes()]);
      setRows(n);
      setTemplates(t.filter((x) => x.status === 'ACTIVE'));
      setRoutes(r.filter((x) => x.enabled));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => rows.filter((r) => statusFilter === 'ALL' || r.status === statusFilter), [rows, statusFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      {canRequest && (
        <div style={{ marginBottom: 16 }}>
          <Button variant="secondary" onClick={() => setShowSandboxForm((v) => !v)} style={{ padding: '0 14px' }}>
            {showSandboxForm ? 'Tutup Form Uji Coba' : 'Kirim Uji Coba (Sandbox)'}
          </Button>
          {showSandboxForm && (
            <SandboxSendForm
              templates={templates}
              routes={routes}
              onSent={() => {
                setShowSandboxForm(false);
                void load();
              }}
            />
          )}
        </div>
      )}
      {!canRequest && <HintNote text="Peran Anda hanya dapat melihat notifikasi (notification.read), tidak dapat mengirim permintaan baru." />}

      {error && <ErrorNote text={error} />}
      {loading && <LoadingNote />}

      {!loading && (
        <>
          <ListToolbar
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'ALL' | NotificationOutboxStatus)} style={{ width: 200 }}>
                <option value="ALL">Semua status</option>
                <option value="REQUESTED">Diminta / Menunggu</option>
                <option value="SENT">Terkirim (SANDBOX)</option>
                <option value="DEAD_LETTER">Dead-Letter</option>
                <option value="BLOCKED">Diblokir</option>
              </Select>
            }
          />
          {rows.length === 0 && <EmptyState text="Belum ada notifikasi yang diminta." />}
          {rows.length > 0 && total === 0 && <EmptyState text="Tidak ada notifikasi yang cocok dengan filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Jenis</th>
                  <th style={thStyle}>Kategori</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Percobaan</th>
                  <th style={thStyle}>Diminta</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((n) => (
                  <ClickableRow key={n.notification_id} onClick={() => setSelected(n)}>
                    <td style={tdStyle}>{n.notification_type}</td>
                    <td style={tdStyle}>{n.category === 'MARKETING' ? 'Pemasaran' : 'Transaksional'}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={STATUS_LABEL[n.status]} />
                    </td>
                    <td style={tdStyle}>{n.attempt_count}</td>
                    <td style={tdStyle}>{formatDateTime(n.requested_at)}</td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}

      <NotificationDetailDrawer
        notification={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        canRetry={canRetry}
        onRetried={() => void load()}
      />
    </div>
  );
}

function SandboxSendForm({
  templates,
  routes,
  onSent,
}: {
  templates: NotificationTemplate[];
  routes: RecipientRouteMasked[];
  onSent: () => void;
}) {
  const { activeBranchId } = useBranch();
  const [templateId, setTemplateId] = useState(templates[0]?.template_id ?? '');
  const [routeId, setRouteId] = useState(routes[0]?.route_id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async () => {
    if (!templateId || !routeId) {
      setError('Pilih template dan rute penerima terlebih dahulu.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const route = routes.find((r) => r.route_id === routeId);
      // Fresh idempotency key per click — this button represents a new
      // intentional test send each time, not a retry of a single request.
      const id = await requestNotification({
        branchId: route?.branch_id ?? activeBranchId,
        notificationType: 'SANDBOX_TEST',
        category: 'TRANSACTIONAL',
        templateId,
        routeId,
        payload: { report_type: 'TEST', branch: 'Sandbox', period: new Date().toISOString().slice(0, 10), amount: '123456' },
        priority: 'NORMAL',
        idempotencyKey: crypto.randomUUID(),
      });
      setSuccess(`Permintaan tercatat (notification_id: ${id.slice(0, 8)}...). Lihat status di daftar/detail.`);
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="alex-card" style={{ padding: 16, marginTop: 10, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 240 }}>
        <FieldLabel label="Template">
          <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            {templates.length === 0 && <option value="">Tidak ada template aktif</option>}
            {templates.map((t) => (
              <option key={t.template_id} value={t.template_id}>
                {t.template_code} (v{t.version})
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 260 }}>
        <FieldLabel label="Rute Penerima">
          <Select value={routeId} onChange={(e) => setRouteId(e.target.value)}>
            {routes.length === 0 && <option value="">Tidak ada rute aktif</option>}
            {routes.map((r) => (
              <option key={r.route_id} value={r.route_id}>
                {(r.role_code ?? (r.user_id ? 'Pengguna' : 'Pelanggan')) + ' — ' + r.address_ref_masked + ' — ' + r.masking_policy}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <Button variant="primary" loading={busy} onClick={() => void submit()} disabled={!templateId || !routeId}>
        Kirim (Sandbox)
      </Button>
      {success && <span style={{ color: 'var(--alex-success)', fontSize: 12, fontWeight: 600 }}>{success}</span>}
      {error && <ErrorNote text={error} />}
    </div>
  );
}

function NotificationDetailDrawer({
  notification,
  open,
  onClose,
  canRetry,
  onRetried,
}: {
  notification: NotificationOutboxRow | null;
  open: boolean;
  onClose: () => void;
  canRetry: boolean;
  onRetried: () => void;
}) {
  const [attempts, setAttempts] = useState<DeliveryAttempt[]>([]);
  const [auditTrail, setAuditTrail] = useState<NotificationAuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  useEffect(() => {
    if (!notification) return;
    setLoading(true);
    setError(null);
    setRetryError(null);
    Promise.all([listDeliveryAttempts(notification.notification_id), getNotificationAuditTrail(notification.notification_id)])
      .then(([a, t]) => {
        setAttempts(a);
        setAuditTrail(t);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [notification?.notification_id]);

  if (!notification) return null;

  const doRetry = async () => {
    setRetryBusy(true);
    setRetryError(null);
    try {
      await retryNotification(notification.notification_id);
      onRetried();
      // Refresh this drawer's own attempt/audit list too.
      const [a, t] = await Promise.all([listDeliveryAttempts(notification.notification_id), getNotificationAuditTrail(notification.notification_id)]);
      setAttempts(a);
      setAuditTrail(t);
    } catch (e) {
      setRetryError(e instanceof Error ? e.message : String(e));
    } finally {
      setRetryBusy(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={`Notifikasi — ${notification.notification_type}`}>
      <DrawerField label="Status" value={<StatusBadge status={STATUS_LABEL[notification.status]} />} />
      <DrawerField label="Kategori" value={notification.category === 'MARKETING' ? 'Pemasaran' : 'Transaksional'} />
      <DrawerField label="Jumlah Percobaan" value={String(notification.attempt_count)} />
      {notification.last_error && <DrawerField label="Error Terakhir" value={notification.last_error} />}
      {notification.dead_letter_reason && <ErrorNote text={`Dead-letter: ${notification.dead_letter_reason} (semua bukti percobaan tetap tersimpan di bawah)`} />}
      {notification.status === 'BLOCKED' && <HintNote text="Diblokir sebelum ada panggilan provider — bisa karena kill switch aktif atau consent pemasaran belum diberikan." />}
      <DrawerField label="notification_id" value={<code style={{ fontSize: 11 }}>{notification.notification_id}</code>} />
      {notification.source_report_run_id && (
        <DrawerField label="Sumber (report_run_id)" value={<code style={{ fontSize: 11 }}>{notification.source_report_run_id}</code>} />
      )}

      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}

      {!loading && notification.status === 'REQUESTED' && canRetry && (
        <div style={{ marginTop: 10 }}>
          <Button variant="primary" loading={retryBusy} onClick={() => void doRetry()}>
            Coba Kirim Ulang Sekarang
          </Button>
          {retryError && <ErrorNote text={retryError} />}
        </div>
      )}

      {!loading && (
        <div style={{ marginTop: 14 }}>
          <GroupLabel>Riwayat Percobaan Pengiriman ({attempts.length})</GroupLabel>
          {attempts.length === 0 && <EmptyState text="Belum ada percobaan pengiriman (mis. karena diblokir sebelum sempat mencoba)." variant="section" />}
          {attempts.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Hasil</th>
                  <th style={thStyle}>Bisa Dicoba Ulang</th>
                  <th style={thStyle}>Waktu</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => (
                  <tr key={a.attempt_id}>
                    <td style={tdStyle}>{a.attempt_no}</td>
                    <td style={tdStyle}>{a.result}</td>
                    <td style={tdStyle}>{a.retryable ? 'Ya' : 'Tidak'}</td>
                    <td style={tdStyle}>{formatDateTime(a.finished_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {attempts.length > 0 && attempts[attempts.length - 1].provider_response?.rendered_body_preview != null && (
            <p style={{ fontSize: 11, color: 'var(--alex-text-muted)', marginTop: 8 }}>
              Pratinjau isi pesan (sesuai kebijakan masking rute):{' '}
              <i>{String(attempts[attempts.length - 1].provider_response.rendered_body_preview)}</i>
            </p>
          )}
        </div>
      )}

      {!loading && auditTrail.length > 0 && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
          <GroupLabel>Jejak Audit</GroupLabel>
          {auditTrail.map((ev) => (
            <div key={ev.event_id} style={{ padding: '6px 0', borderBottom: '1px solid var(--alex-border-soft)', fontSize: 11.5 }}>
              <b>{ev.action}</b> · {formatDateTime(ev.occurred_at)}
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}

function RoutesTab({ canManage }: { canManage: boolean }) {
  const [routes, setRoutes] = useState<RecipientRouteMasked[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRoutes(await listRecipientRoutes());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div>
      <p style={{ fontSize: 11, color: 'var(--alex-text-muted)', marginTop: 0 }}>
        Nomor/alamat penuh hanya terlihat oleh pemegang izin notification.route_manage pada cakupan cabang terkait — peran lain melihat versi
        tersamar di bawah ini, tidak pernah nomor mentah.
      </p>
      {canManage && (
        <Button variant="secondary" onClick={() => setShowForm((v) => !v)} style={{ padding: '0 14px', marginBottom: 12 }}>
          {showForm ? 'Tutup Form' : 'Tambah Rute Penerima'}
        </Button>
      )}
      {showForm && canManage && <RouteForm onSaved={() => { setShowForm(false); void load(); }} />}
      {!canManage && <HintNote text="Peran Anda tidak dapat mengelola rute penerima (notification.route_manage)." />}

      {error && <ErrorNote text={error} />}
      {loading && <LoadingNote />}
      {!loading && routes.length === 0 && <EmptyState text="Belum ada rute penerima." />}
      {!loading && routes.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Target</th>
              <th style={thStyle}>Kanal</th>
              <th style={thStyle}>Alamat (tersamar)</th>
              <th style={thStyle}>Kebijakan Masking</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {routes.map((r) => (
              <tr key={r.route_id}>
                <td style={tdStyle}>{r.role_code ?? (r.user_id ? 'Pengguna tertentu' : 'Pelanggan')}</td>
                <td style={tdStyle}>{r.channel}</td>
                <td style={tdStyle}>
                  <code style={{ fontSize: 11.5 }}>{r.address_ref_masked}</code>
                  {!r.can_view_full && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--alex-text-faint)' }}>(tersamar)</span>}
                </td>
                <td style={tdStyle}>{r.masking_policy}</td>
                <td style={tdStyle}>
                  <StatusBadge status={r.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RouteForm({ onSaved }: { onSaved: () => void }) {
  const { branches } = useBranch();
  const [scope, setScope] = useState<'ROLE' | 'TENANT_WIDE_ROLE'>('ROLE');
  const [branchId, setBranchId] = useState(branches[0]?.branch_id ?? '');
  const [roleCode, setRoleCode] = useState('MANAGER');
  const [addressRef, setAddressRef] = useState('');
  const [maskingPolicy, setMaskingPolicy] = useState<'FULL' | 'SUMMARY_ONLY' | 'MASKED'>('FULL');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async () => {
    if (!addressRef.trim()) {
      setError('Isi alamat/nomor tujuan.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await upsertRecipientRoute({
        branchId: scope === 'TENANT_WIDE_ROLE' ? null : branchId,
        roleCode,
        channel: 'WHATSAPP',
        addressRef: addressRef.trim(),
        maskingPolicy,
        enabled: true,
      });
      setSuccess('Rute penerima berhasil disimpan.');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="alex-card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 180 }}>
        <FieldLabel label="Cakupan">
          <Select value={scope} onChange={(e) => setScope(e.target.value as 'ROLE' | 'TENANT_WIDE_ROLE')}>
            <option value="ROLE">Per Cabang</option>
            <option value="TENANT_WIDE_ROLE">Seluruh Tenant</option>
          </Select>
        </FieldLabel>
      </div>
      {scope === 'ROLE' && (
        <div style={{ width: 200 }}>
          <FieldLabel label="Cabang">
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {branches.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
      )}
      <div style={{ width: 160 }}>
        <FieldLabel label="Peran Tujuan">
          <Select value={roleCode} onChange={(e) => setRoleCode(e.target.value)}>
            <option value="MANAGER">Manager</option>
            <option value="TENANT_OWNER">Owner</option>
            <option value="SYSTEM_ADMIN">System Admin</option>
            <option value="AUDITOR">Auditor</option>
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 200 }}>
        <FieldLabel label="Nomor WhatsApp">
          <TextInput value={addressRef} onChange={(e) => setAddressRef(e.target.value)} placeholder="+62812xxxxxxx" />
        </FieldLabel>
      </div>
      <div style={{ width: 170 }}>
        <FieldLabel label="Kebijakan Masking">
          <Select value={maskingPolicy} onChange={(e) => setMaskingPolicy(e.target.value as 'FULL' | 'SUMMARY_ONLY' | 'MASKED')}>
            <option value="FULL">Penuh</option>
            <option value="SUMMARY_ONLY">Ringkasan Saja</option>
            <option value="MASKED">Tersamar</option>
          </Select>
        </FieldLabel>
      </div>
      <Button variant="primary" loading={busy} onClick={() => void submit()}>
        Simpan Rute
      </Button>
      {success && <span style={{ color: 'var(--alex-success)', fontSize: 12, fontWeight: 600 }}>{success}</span>}
      {error && <ErrorNote text={error} />}
    </div>
  );
}

function TemplatesTab({ canManage }: { canManage: boolean }) {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setTemplates(await listTemplates());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div>
      {canManage && (
        <Button variant="secondary" onClick={() => setShowForm((v) => !v)} style={{ padding: '0 14px', marginBottom: 12 }}>
          {showForm ? 'Tutup Form' : 'Buat / Versi Baru Template'}
        </Button>
      )}
      {showForm && canManage && <TemplateForm onSaved={() => { setShowForm(false); void load(); }} />}
      {!canManage && <HintNote text="Peran Anda tidak dapat mengelola template (notification.template_manage)." />}

      {error && <ErrorNote text={error} />}
      {loading && <LoadingNote />}
      {!loading && templates.length === 0 && <EmptyState text="Belum ada template." />}
      {!loading && templates.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Kode</th>
              <th style={thStyle}>Kanal</th>
              <th style={thStyle}>Versi</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Isi</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.template_id}>
                <td style={tdStyle}>{t.template_code}</td>
                <td style={tdStyle}>{t.channel}</td>
                <td style={tdStyle}>v{t.version}</td>
                <td style={tdStyle}>
                  <StatusBadge status={t.status} />
                </td>
                <td style={{ ...tdStyle, fontSize: 11 }}>{t.body}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TemplateForm({ onSaved }: { onSaved: () => void }) {
  const [templateCode, setTemplateCode] = useState('');
  const [eventType, setEventType] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async () => {
    if (!templateCode.trim() || !eventType.trim() || !body.trim()) {
      setError('Lengkapi kode template, jenis event, dan isi pesan.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      // Variables auto-detected from {{name}} tokens in the body — matches
      // exactly what fn_render_notification_body substitutes server-side.
      const names = Array.from(new Set(Array.from(body.matchAll(/\{\{(\w+)\}\}/g)).map((m) => m[1])));
      await createTemplateVersion({
        templateCode: templateCode.trim(),
        channel: 'WHATSAPP',
        eventType: eventType.trim(),
        body,
        variables: names.map((n) => ({ name: n, sensitive: n === 'amount' })),
      });
      setSuccess('Template tersimpan sebagai versi baru (ACTIVE).');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="alex-card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 180 }}>
        <FieldLabel label="Kode Template">
          <TextInput value={templateCode} onChange={(e) => setTemplateCode(e.target.value)} placeholder="REPORT_DAILY_READY" />
        </FieldLabel>
      </div>
      <div style={{ width: 180 }}>
        <FieldLabel label="Jenis Event">
          <TextInput value={eventType} onChange={(e) => setEventType(e.target.value)} placeholder="REPORT_DAILY_READY" />
        </FieldLabel>
      </div>
      <div style={{ width: 320 }}>
        <FieldLabel label="Isi Pesan (gunakan {{variabel}})">
          <TextInput value={body} onChange={(e) => setBody(e.target.value)} placeholder="Laporan {{report_type}} siap. Total: {{amount}}" />
        </FieldLabel>
      </div>
      <Button variant="primary" loading={busy} onClick={() => void submit()}>
        Simpan Versi Baru
      </Button>
      {success && <span style={{ color: 'var(--alex-success)', fontSize: 12, fontWeight: 600 }}>{success}</span>}
      {error && <ErrorNote text={error} />}
    </div>
  );
}

function KillSwitchTab({ canManage }: { canManage: boolean }) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const ks = await getKillSwitch();
      setEnabled(ks?.enabled ?? false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const toggle = async () => {
    if (enabled === null) return;
    setBusy(true);
    setError(null);
    try {
      await setKillSwitch(!enabled);
      setEnabled(!enabled);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title="Outbound Kill Switch">
      <p style={{ fontSize: 11.5, color: 'var(--alex-text-muted)', marginTop: 0 }}>
        Saat AKTIF, tidak ada permintaan notifikasi baru yang benar-benar memanggil provider — permintaan tetap tercatat & teraudit dengan status
        "Diblokir", bukan gagal diam-diam. Server-authoritative: peran tanpa notification.kill_switch_manage tidak dapat mengubah ini walau lewat
        panggilan langsung ke tabel.
      </p>
      {error && <ErrorNote text={error} />}
      {loading && <LoadingNote />}
      {!loading && enabled !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <StatusBadge status={enabled ? 'AKTIF (outbound diblokir)' : 'NONAKTIF (pengiriman normal)'} />
          {canManage ? (
            <Button variant={enabled ? 'secondary' : 'danger'} loading={busy} onClick={() => void toggle()}>
              {enabled ? 'Nonaktifkan Kill Switch' : 'Aktifkan Kill Switch'}
            </Button>
          ) : (
            <HintNote text="Peran Anda hanya dapat melihat status ini (notification.kill_switch_manage diperlukan untuk mengubah)." />
          )}
        </div>
      )}
    </SectionCard>
  );
}
